'use client';

import { ComponentProps, type ReactNode, forwardRef } from 'react';

import { tv } from '../_lib/utils';
import { Button } from '../Inputs/Button';
import { isValidRichText } from '../_lib/isValidRichText';
import { useForm } from 'react-hook-form';
import { Text } from '../Text';

const styles = tv({
	slots: {
		base: 'rounded-[2.06rem] transition-all duration-normal ease-smooth w-full flex flex-row items-center p-[0.38rem] pl-[1.13rem] border focus-within:border-purple-100/80 focus-within:text-purple-100/80 hover:border-purple-100/60 hover:text-purple-100/80 bg-white text-purple-100/70 border-purple-100/40',
		input: 'w-full disabled:cursor-not-allowed',
	},
	variants: {
		hasValue: {
			true: {
				base: 'border-purple-100/60 text-purple-100/80',
			},
		},
		disabled: {
			true: {
				base: 'border-grey-60 bg-grey-20 text-grey-60',
			},
		},
	},
});

type LabelProps = {
	hasValue?: boolean;
	disabled?: boolean;
	button?: string;
	copy?: ReactNode;
};

export type SignUpProps = Omit<ComponentProps<'input'>, 'name' | 'id' | 'ref'> & {
	id?: string;
	name?: string;
	successHeading?: string;
	successMessage?: string;
	hubspotId: string;
} & LabelProps;

export const SignUp = forwardRef<HTMLInputElement, SignUpProps>(function SignUp({ className, hasValue, disabled, ...props }, ref) {
	const {
		register,
		reset,
		handleSubmit,
		formState: { isSubmitSuccessful },
	} = useForm({
		defaultValues: {
			email: '',
		},
	});
	const { base, input } = styles({ hasValue, disabled });

	const onSubmit = handleSubmit(async data => {
		await fetch(`/api/hubspot/forms?formId=${props.hubspotId}`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(data),
		});

		reset();
	});

	return (
		<div className='max-w-[26rem]'>
			<form method='POST' onSubmit={onSubmit} aria-hidden={isSubmitSuccessful}>
				{isSubmitSuccessful && (
					<div className={'flex flex-col'}>
						<Text as='span' styleType='heading-5xs'>
							{props.successHeading ?? 'Thank you!'}
						</Text>
						<span className={'pt-2 pb-4 opacity-80'}>{props.successMessage ?? 'We will get back to you as soon as possible.'}</span>
					</div>
				)}
				<label data-component='SignUp' className={`block relative w-full ${base()} ${className || ''}`}>
					<input
						className={`text-body-sm pr-2 ${input()}`}
						{...props}
						placeholder='Enter your email'
						type={props.type || 'text'}
						disabled={disabled}
						{...register('email', {
							required: true,
							disabled: isSubmitSuccessful,
							pattern: {
								value:
									/^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
								message: 'My valid email is',
							},
						})}
					/>
					{props.button && (
						<Button parent='SignUp' type='submit' disabled={isSubmitSuccessful} styleSize='medium' className='h-fit whitespace-nowrap'>
							{props.button}
						</Button>
					)}
				</label>
				{isValidRichText(props.copy) && (
					<Text className={'px-2 pt-2 text-center [&_a]:underline'} styleType='body-xs'>
						{props.copy}
					</Text>
				)}
			</form>
		</div>
	);
});
